import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Globe, Code, Brain, Sparkles, ExternalLink, Github, 
  Star, Zap, GraduationCap, Bus, ShoppingCart, Stethoscope, Palette,
  Calculator, Music, Vote, Mail, Lock, GitBranch, ArrowRight, LayoutGrid
} from 'lucide-react';
import { projectsData } from '../data/projectsData';
import ProjectDetailsPage from '../components/ProjectDetailsPage';
import { ProjectShowcase } from '../components/ProjectShowcase';
import { Section, SectionHeader } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';

export default function Projects() {
  const [activeCategory, setActiveCategory] = useState('fullstack');
  const [viewMode, setViewMode] = useState('showcase'); // 'showcase' | 'grid'
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const iconMap = {
    Globe: Globe,
    Code: Code,
    Brain: Brain,
    Sparkles: Sparkles,
    Zap: Zap,
    GraduationCap: GraduationCap,
    Bus: Bus,
    ShoppingCart: ShoppingCart,
    Stethoscope: Stethoscope,
    Palette: Palette,
    Calculator: Calculator,
    Music: Music,
    Vote: Vote,
    Mail: Mail,
    Lock: Lock,
    GitBranch: GitBranch
  };

  const isValidLiveUrl = (url) => {
    if (!url || url === '#') return false;
    const placeholderUrls = [
      'https://your-ecommerce-demo.com',
      'https://nexttrip-demo.com',
      'https://doctor-appointment-demo.com',
      'https://your-sgpa-calculator.com',
      'https://music-genre-classifier-demo.com',
      'https://blind-voting-system-demo.com',
      'https://brain-stroke-prediction-demo.com',
      'https://automated-email-responder-demo.com',
      'https://smart-door-lock-demo.com'
    ];
    return !placeholderUrls.includes(url);
  };

  const categories = Object.keys(projectsData);

  const handleBackToProjects = () => {
    setShowDetails(false);
    setSelectedProject(null);
  };

  if (showDetails && selectedProject) {
    return (
      <ProjectDetailsPage 
        project={selectedProject} 
        onBack={handleBackToProjects}
      />
    );
  }

  return (
    <Section id="projects">
      <SectionHeader
        title="Featured Projects"
        subtitle="Full stack applications, machine learning models, and software engineering tools."
      />

      {/* Category Tabs & View Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-3 border-b border-[var(--bg-recessed)]">
        <div className="flex flex-wrap items-center gap-1">
          {categories.map((category) => {
            const categoryData = projectsData[category];
            const Icon = iconMap[categoryData.icon] || Code;
            const isActive = activeCategory === category;
            
            return (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  isActive
                    ? 'text-[var(--fg-primary)] bg-[var(--bg-hover)]'
                    : 'text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <Icon size={16} />
                <span>{categoryData.title}</span>
              </button>
            );
          })}
        </div>

        {/* View Mode Toggle: Showcase vs Grid */}
        <div className="flex items-center self-start sm:self-auto p-1 rounded-lg bg-[var(--bg-recessed)] shadow-[var(--border-ring)]">
          <button
            type="button"
            onClick={() => setViewMode('showcase')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              viewMode === 'showcase'
                ? 'bg-[var(--bg-elevated)] text-[var(--fg-primary)] shadow-[var(--shadow-sm)]'
                : 'text-[var(--fg-secondary)] hover:text-[var(--fg-primary)]'
            }`}
          >
            <Sparkles size={13} />
            <span>Showcase</span>
          </button>

          <button
            type="button"
            onClick={() => setViewMode('grid')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${
              viewMode === 'grid'
                ? 'bg-[var(--bg-elevated)] text-[var(--fg-primary)] shadow-[var(--shadow-sm)]'
                : 'text-[var(--fg-secondary)] hover:text-[var(--fg-primary)]'
            }`}
          >
            <LayoutGrid size={13} />
            <span>Grid</span>
          </button>
        </div>
      </div>

      {viewMode === 'showcase' ? (
        <ProjectShowcase projects={projectsData[activeCategory].projects} />
      ) : (
        /* Uniform Card grid at 24px gaps */
        <div
          className={`grid gap-6 ${
            projectsData[activeCategory].projects.length === 1
              ? 'max-w-md mx-auto'
              : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}
        >
          {projectsData[activeCategory].projects.map((project) => {
            const IconComp = iconMap[project.image] || Code;
            const projectSlug = project.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
            const projectPath = `/project/${project.id}/${projectSlug}`;
            const hasRealImage = project.images && project.images[0] && typeof project.images[0] === 'string' && !project.images[0].includes('Image_not_available');

            return (
              <Card key={project.id} interactive className="flex flex-col justify-between h-full">
                <div>
                  {/* Header & Badges */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-md bg-[var(--bg-recessed)] text-[var(--fg-primary)] shadow-[var(--border-ring)]">
                        <IconComp size={20} />
                      </div>
                      <div>
                        <h3 className="t-h5 leading-tight">
                          <Link
                            to={projectPath}
                            onClick={() => sessionStorage.setItem('selectedProject', JSON.stringify(project))}
                            className="text-[var(--fg-primary)] hover:underline decoration-[var(--border-ring)]"
                          >
                            {project.title}
                          </Link>
                        </h3>
                        <div className="mt-1">
                          <span className="t-mono text-[11px] text-[var(--fg-secondary)]">{project.status}</span>
                        </div>
                      </div>
                    </div>

                    {project.featured && (
                      <Badge className="flex items-center gap-1 text-[10px] py-0.5">
                        <Star size={10} className="fill-[var(--fg-primary)] text-[var(--fg-primary)]" />
                        Featured
                      </Badge>
                    )}
                  </div>

                  {/* Recessed Screenshot Frame */}
                  <Link
                    to={projectPath}
                    onClick={() => sessionStorage.setItem('selectedProject', JSON.stringify(project))}
                    className="block bg-[var(--bg-recessed)] shadow-[var(--border-ring)] rounded-lg p-2 mb-4 hover:opacity-90 transition-opacity cursor-pointer group"
                  >
                    <div className="w-full aspect-[16/9] bg-[var(--bg-canvas)] rounded overflow-hidden flex items-center justify-center">
                      {hasRealImage ? (
                        <img
                          src={project.images[0]}
                          alt={project.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="p-4 text-center">
                          <IconComp size={32} className="mx-auto text-[var(--fg-muted)] mb-1" />
                          <span className="text-xs text-[var(--fg-secondary)] font-mono">{project.title}</span>
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Description */}
                  <p className="text-sm text-[var(--fg-secondary)] leading-relaxed mb-4 line-clamp-3">
                    {project.description}
                  </p>

                  {/* Tech Stack Badges */}
                  <div className="flex flex-wrap gap-1.5 mb-6">
                    {project.tech.slice(0, 4).map((t) => (
                      <Badge key={t}>{t}</Badge>
                    ))}
                    {project.tech.length > 4 && (
                      <Badge>+{project.tech.length - 4}</Badge>
                    )}
                  </div>
                </div>

                {/* Action Links */}
                <div className="pt-4 border-t border-[var(--bg-recessed)] space-y-3">
                  <div className="flex items-center gap-2">
                    {isValidLiveUrl(project.liveUrl) ? (
                      <Button
                        asChild
                        variant="secondary"
                        size="sm"
                        className="flex-1"
                      >
                        <a href={project.liveUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink size={14} className="mr-1.5" />
                          Live Demo
                        </a>
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 opacity-50 cursor-not-allowed"
                        disabled
                      >
                        <ExternalLink size={14} className="mr-1.5" />
                        Live Demo
                      </Button>
                    )}

                    <Button
                      asChild
                      variant="ghost"
                      size="sm"
                    >
                      <a href={project.githubUrl} target="_blank" rel="noopener noreferrer">
                        <Github size={14} className="mr-1.5" />
                        Code
                      </a>
                    </Button>
                  </div>

                  <Link
                    to={projectPath}
                    onClick={() => {
                      sessionStorage.setItem('selectedProject', JSON.stringify(project));
                    }}
                    className="inline-flex items-center text-xs font-medium text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] transition-colors cursor-pointer"
                  >
                    <span>See Details</span>
                    <ArrowRight size={12} className="ml-1" />
                  </Link>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Section>
  );
}
